import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, Send, Calendar, ChevronRight, ArrowRight, MessageSquare } from 'lucide-react';
import { contactAPI } from '../lib/supabase';
import { emailService } from '../services/emailService';
import appointmentService from '../services/appointmentService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });

  const [appointmentData, setAppointmentData] = useState({
    service: '',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    vehicle: '',
  });

  const [activeTab, setActiveTab] = useState('contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [appointmentSubmitting, setAppointmentSubmitting] = useState(false);
  const [appointmentSubmitted, setAppointmentSubmitted] = useState(false);
  const [appointmentError, setAppointmentError] = useState('');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const inquiryData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.service ? `${formData.service} Request` : 'General Inquiry',
        message: `Service: ${formData.service || 'Not specified'}\nPreferred Date: ${formData.preferredDate || 'Not specified'}\nPreferred Time: ${formData.preferredTime || 'Not specified'}\n\nMessage: ${formData.message || 'No additional message'}`,
        inquiryType: 'general'
      };
      
      await contactAPI.addInquiry(inquiryData);
      
      const emailResult = await emailService.sendContactNotification(formData);
      if (!emailResult.success) {
        console.warn('Email notification failed:', emailResult.error);
      }
      
      await emailService.sendAutoReply(formData.email, formData.name, 'contact inquiry');
      
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17797421129/LrUYCIi_tdMbEMmwvKZC'
        });
      }
      
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', service: '', preferredDate: '', preferredTime: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Contact form submission error:', error);
      alert('There was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    setAppointmentSubmitting(true);
    setAppointmentError('');

    try {
      const payload = {
        serviceType: appointmentData.service,
        date: appointmentData.date,
        time: appointmentData.time,
        name: appointmentData.name,
        email: appointmentData.email,
        phone: appointmentData.phone,
        vehicleInfo: appointmentData.vehicle,
        notes: ''
      };

      const result = await appointmentService.bookAppointment(payload);
      if (!result.success) throw new Error(result.error || 'Failed to book appointment');

      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17797421129/LrUYCIi_tdMbEMmwvKZC'
        });
      }

      setAppointmentSubmitted(true);
      setTimeout(() => {
        setAppointmentSubmitted(false);
        setAppointmentData({ service: '', date: '', time: '', name: '', email: '', phone: '', vehicle: '' });
      }, 3000);
    } catch (err) {
      console.error('Appointment booking error:', err);
      setAppointmentError(err.message || 'Failed to book appointment');
    } finally {
      setAppointmentSubmitting(false);
    }
  };

  const handleContactChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleAppointmentChange = (e) => setAppointmentData({ ...appointmentData, [e.target.name]: e.target.value });

  const services = [
    'Sales Consultation', 'Test Drive', 'Vehicle Service', 'Financing Discussion',
    'Trade-In Appraisal', 'Parts & Accessories', 'Warranty Service', 'General Inquiry',
  ];

  const timeSlots = ['10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'];

  const hours = [
    { day: 'Monday', time: '9:00 AM - 5:00 PM', open: true },
    { day: 'Tuesday', time: '9:00 AM - 5:00 PM', open: true },
    { day: 'Wednesday', time: '9:00 AM - 5:00 PM', open: true },
    { day: 'Thursday', time: '9:00 AM - 5:00 PM', open: true },
    { day: 'Friday', time: '9:00 AM - 5:00 PM', open: true },
    { day: 'Saturday', time: '10:00 AM - 2:00 PM', open: true, note: 'Dealership Only' },
    { day: 'Sunday', time: 'Closed', open: false },
  ];

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  const inputClasses = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/50 transition-all";
  const selectClasses = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/50 transition-all appearance-none";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>Contact Paradise Performance | Directions & Hours | Paradise TX</title>
        <meta name="description" content="Reach Paradise Performance at (817) 382-2316 or stop by 305 Co Rd 3470 in Paradise TX. Open Mon-Fri 9-5 and Sat 10-3. Schedule a visit online." />
        <link rel="canonical" href="https://paradise-performance.com/contact" />
        <meta property="og:title" content="Get in Touch with Paradise Performance" />
        <meta property="og:description" content="A small-town shop that picks up the phone. Call, text or drop by our Paradise TX location." />
        <meta property="og:url" content="https://paradise-performance.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Paradise Performance",
            "url": "https://paradise-performance.com/contact",
            "mainEntity": {
              "@type": "AutoDealer",
              "name": "Paradise Performance LLC",
              "telephone": "+18173822316",
              "email": "sales@paradise-performance.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "305 Co Rd 3470",
                "addressLocality": "Paradise",
                "addressRegion": "TX",
                "postalCode": "76073"
              },
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  "opens": "09:00",
                  "closes": "17:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": "Saturday",
                  "opens": "10:00",
                  "closes": "14:00",
                  "description": "Dealership sales only - service shop closed"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Hero — Full-width map background with overlay */}
      <section className="relative min-h-[60vh] lg:min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/assets/images/new/brakes/brake-repair-hero.webp"
            alt="Paradise Performance Auto Service"
            className="w-full h-full object-cover"
            style={{ filter: 'grayscale(0.3) brightness(0.4)' }}
          />
        </div>
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent pointer-events-none" />
        
        <div className="relative z-10 container mx-auto px-6 pb-16 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-red-500 uppercase tracking-[0.3em] text-sm font-medium mb-4">Get in Touch</p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1]">
              We pick up<br />the phone.
            </h1>
            <p className="text-gray-400 text-lg mt-6 max-w-lg">
              No phone trees. No hold music. Call us and talk to a real person who knows cars.
            </p>
          </motion.div>

          {/* Quick action cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 max-w-3xl"
          >
            <a
              href="tel:+18173822316"
              className="group flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-red-600/20 hover:border-red-500/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/30 transition-colors">
                <Phone className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">(817) 382-2316</p>
                <p className="text-gray-500 text-xs">Call now</p>
              </div>
            </a>

            <a
              href="mailto:sales@paradise-performance.com"
              className="group flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-red-600/20 hover:border-red-500/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/30 transition-colors">
                <Mail className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm truncate">sales@paradise-performance.com</p>
                <p className="text-gray-500 text-xs">Email us</p>
              </div>
            </a>

            <a
              href="https://www.google.com/maps/search/?api=1&query=Paradise+Performance+305+Co+Rd+3470+Paradise+TX+76073"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-red-600/20 hover:border-red-500/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/30 transition-colors">
                <MapPin className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">305 Co Rd 3470</p>
                <p className="text-gray-500 text-xs">Paradise, TX 76073</p>
              </div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Main Content — Two column: form left, info right */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">

            {/* Left column — Form */}
            <div className="lg:col-span-7">
              {/* Tab toggle */}
              <div className="flex gap-1 mb-10 bg-white/5 rounded-xl p-1 max-w-xs">
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'contact'
                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/25'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  Message
                </button>
                <button
                  onClick={() => setActiveTab('appointment')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'appointment'
                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/25'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Appointment
                </button>
              </div>

              <AnimatePresence mode="wait">
                {activeTab === 'contact' && (
                  <motion.div
                    key="contact"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2">Send a Message</h2>
                    <p className="text-gray-500 mb-8">Tell us what you need. We respond within a few hours during business days.</p>

                    <form onSubmit={handleContactSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Name *</label>
                          <input type="text" name="name" value={formData.name} onChange={handleContactChange} className={inputClasses} placeholder="John Doe" required />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Email *</label>
                          <input type="email" name="email" value={formData.email} onChange={handleContactChange} className={inputClasses} placeholder="john@email.com" required />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Phone</label>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleContactChange} className={inputClasses} placeholder="(817) 555-0123" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">What do you need?</label>
                          <select name="service" value={formData.service} onChange={handleContactChange} className={selectClasses}>
                            <option value="" className="bg-[#111]">Select a service</option>
                            {services.map((s) => <option key={s} value={s} className="bg-[#111]">{s}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Preferred Date</label>
                          <input type="date" name="preferredDate" value={formData.preferredDate} onChange={handleContactChange} className={inputClasses} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Preferred Time</label>
                          <select name="preferredTime" value={formData.preferredTime} onChange={handleContactChange} className={selectClasses}>
                            <option value="" className="bg-[#111]">Select a time</option>
                            {timeSlots.map((t) => <option key={t} value={t} className="bg-[#111]">{t}</option>)}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Message</label>
                        <textarea name="message" value={formData.message} onChange={handleContactChange} rows={4} className={inputClasses} placeholder="Tell us how we can help..." />
                      </div>

                      {submitted ? (
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
                          <p className="text-green-400 font-semibold">Message sent!</p>
                          <p className="text-green-400/70 text-sm">We'll get back to you shortly.</p>
                        </motion.div>
                      ) : (
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="group w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-3"
                        >
                          <Send className="w-4 h-4" />
                          <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                          <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </button>
                      )}
                    </form>
                  </motion.div>
                )}

                {activeTab === 'appointment' && (
                  <motion.div
                    key="appointment"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2">Book an Appointment</h2>
                    <p className="text-gray-500 mb-8">Pick a day that works. We'll confirm within a few hours.</p>

                    {appointmentSubmitted && (
                      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center mb-6">
                        <p className="text-green-400 font-semibold">Appointment booked!</p>
                        <p className="text-green-400/70 text-sm">Confirmation email on its way.</p>
                      </motion.div>
                    )}
                    {appointmentError && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center mb-6">
                        <p className="text-red-400">{appointmentError}</p>
                      </div>
                    )}

                    <form onSubmit={handleAppointmentSubmit} className="space-y-5">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Service Type *</label>
                        <select name="service" value={appointmentData.service} onChange={handleAppointmentChange} className={selectClasses} required>
                          <option value="" className="bg-[#111]">Select a service</option>
                          {services.map((s) => <option key={s} value={s} className="bg-[#111]">{s}</option>)}
                        </select>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Date *</label>
                          <input type="date" name="date" value={appointmentData.date} onChange={handleAppointmentChange} className={inputClasses} required />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Time *</label>
                          <select name="time" value={appointmentData.time} onChange={handleAppointmentChange} className={selectClasses} required>
                            <option value="" className="bg-[#111]">Select a time</option>
                            {timeSlots.map((t) => <option key={t} value={t} className="bg-[#111]">{t}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Name *</label>
                          <input type="text" name="name" value={appointmentData.name} onChange={handleAppointmentChange} className={inputClasses} required />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Email *</label>
                          <input type="email" name="email" value={appointmentData.email} onChange={handleAppointmentChange} className={inputClasses} required />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Phone *</label>
                          <input type="tel" name="phone" value={appointmentData.phone} onChange={handleAppointmentChange} className={inputClasses} required />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Vehicle</label>
                          <input type="text" name="vehicle" value={appointmentData.vehicle} onChange={handleAppointmentChange} className={inputClasses} placeholder="e.g. 2020 VW Tiguan" />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={appointmentSubmitting}
                        className="group w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-3"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>{appointmentSubmitting ? 'Booking...' : 'Book Appointment'}</span>
                        <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right column — Info sidebar */}
            <div className="lg:col-span-5 space-y-8">
              {/* Hours card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="text-white font-bold text-lg">Business Hours</h3>
                </div>
                <div className="space-y-0">
                  {hours.map((h) => (
                    <div
                      key={h.day}
                      className={`flex items-center justify-between py-3 border-b border-white/5 last:border-0 ${
                        h.day === today ? 'bg-red-600/5 -mx-3 px-3 rounded-lg border-red-500/10' : ''
                      }`}
                    >
                      <span className={`text-sm ${h.day === today ? 'text-white font-semibold' : 'text-gray-400'}`}>
                        {h.day}
                        {h.day === today && (
                          <span className="ml-2 text-[10px] bg-red-600/30 text-red-400 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">Today</span>
                        )}
                      </span>
                      <span className={`text-sm font-medium ${h.open ? (h.day === today ? 'text-green-400' : 'text-gray-300') : 'text-gray-600'}`}>
                        {h.time}
                        {h.note && <span className="ml-2 text-[10px] text-yellow-500 uppercase tracking-wider">({h.note})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Address card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="text-white font-bold text-lg">Location</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  305 Co Rd 3470<br />
                  Paradise, TX 76073
                </p>
                <p className="text-gray-500 text-sm mb-5">
                  About 40 minutes northwest of Fort Worth. Take US-81/287 N to FM-2123, then follow the signs toward Paradise.
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Paradise+Performance+305+Co+Rd+3470+Paradise+TX+76073"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 text-sm font-medium transition-colors"
                >
                  Open in Google Maps
                  <ChevronRight className="w-4 h-4" />
                </a>
              </motion.div>

              {/* Quick contact card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-red-600/20 to-red-900/10 border border-red-500/10 rounded-2xl p-6"
              >
                <h3 className="text-white font-bold text-lg mb-3">Prefer a quick call?</h3>
                <p className="text-gray-400 text-sm mb-5">
                  Skip the form. Call Wayne directly and get your questions answered in two minutes.
                </p>
                <a
                  href="tel:+18173822316"
                  className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all"
                >
                  <Phone className="w-4 h-4" />
                  (817) 382-2316
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;